import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Stepper, type StepState } from '../../design-system/components/Stepper';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { breakpoints } from '../../design-system/tokens/breakpoints';

export interface WizardStep {
  number: number;
  label: string;
}

interface WizardProgressProps {
  steps: WizardStep[];
  currentStep: number;
  /** Optional className for the outer wrapper. */
  className?: string;
}

const Layout = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${spacing[8]};
  align-items: flex-start;
  width: 100%;

  ${breakpoints.down.md} {
    flex-direction: column;
    gap: ${spacing[4]};
  }
`;

const StepperColumn = styled.nav`
  flex-shrink: 0;
  width: 220px;
  position: sticky;
  top: ${spacing[2]};

  ${breakpoints.down.md} {
    display: none;
  }
`;

const StepperWrapper = styled.div`
  padding: ${spacing[2]} 0;
`;

const FormColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

const MobileProgress = styled.div`
  display: none;
  width: 100%;
  margin-bottom: ${spacing[2]};

  ${breakpoints.down.md} {
    display: block;
  }
`;

const ProgressMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[2]};
`;

const ProgressLabel = styled.span`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.icomBlue || props.theme.colors.primary};
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const ProgressStepName = styled.span`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.theme.colors.text};
  text-align: right;
`;

const Track = styled.div`
  width: 100%;
  height: 6px;
  border-radius: ${borders.radius.full};
  background: ${props => props.theme.colors.borderLight || props.theme.colors.border};
  overflow: hidden;
`;

const Fill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${props => props.$percent}%;
  border-radius: ${borders.radius.full};
  background: ${props => props.theme.colors.gradientPrimary || props.theme.colors.primary};
  transition: width 0.25s ease;
`;

/**
 * Desktop vertical stepper + mobile progress bar for registration wizards.
 */
export const WizardProgress: React.FC<WizardProgressProps & { children: React.ReactNode }> = ({
  steps,
  currentStep,
  children,
  className,
}) => {
  const { t } = useTranslation();
  const total = steps.length;
  const percent = Math.round((currentStep / total) * 100);
  const currentLabel = steps.find(s => s.number === currentStep)?.label ?? '';

  const stepperSteps = steps.map(s => ({
    id: String(s.number),
    label: s.label,
    state: (currentStep > s.number
      ? 'completed'
      : currentStep === s.number
        ? 'active'
        : 'pending') as StepState,
  }));

  return (
    <Layout className={className}>
      <StepperColumn aria-label={t('register.stepProgress', { current: currentStep, total })}>
        <StepperWrapper>
          <Stepper
            steps={stepperSteps}
            orientation="vertical"
            showLabels
            responsive={false}
            ariaLabel={t('register.progressLabel')}
          />
        </StepperWrapper>
      </StepperColumn>

      <FormColumn>
        <MobileProgress
          role="group"
          aria-label={t('register.stepProgress', { current: currentStep, total })}
        >
          <ProgressMeta>
            <ProgressLabel>
              {t('register.stepProgress', { current: currentStep, total })}
            </ProgressLabel>
            <ProgressStepName>{currentLabel}</ProgressStepName>
          </ProgressMeta>
          <Track
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={total}
            aria-valuenow={currentStep}
            aria-valuetext={t('register.stepProgress', { current: currentStep, total })}
          >
            <Fill $percent={percent} />
          </Track>
        </MobileProgress>
        {children}
      </FormColumn>
    </Layout>
  );
};

export default WizardProgress;
